import { DatePipe, Location, NgIf, NgStyle, AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, Inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import {
  PageDetailComponent,
  PrintPageComponent,
  SummaryCardComponent,
} from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe, AgrDatePipe } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { SettingsStorage } from '@next/core-lib/settings';
import { TableColumn, TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { AgrUser, SelectedCustomer, SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, take, withLatestFrom } from 'rxjs/operators';

import { PoultryContextKeys } from 'src/app/shared/constants';

import { SettingsServiceHelper } from '../../../shared/services/settingsHelperService';
import {
  AnimalType,
  ColumnSettings,
  FeedUnit,
  ReferenceValueSheet,
  ReferenceValueSheetRow,
  TemperatureUnit,
  ViewMode,
  WaterUnit,
  WeightUnit,
} from '../../models/reference-value-sheet.model';

import * as refValSheetActions from '../../store/reference-value-sheet.actions';

import {
  getColumnSettings,
  getReferenceValueSheets,
} from '../../store/reference-value-sheet.selectors';
import { ColumnSettingsComponent } from '../column-settings/column-settings.component';

@Component({
  selector: 'app-create-reference-value-sheet',
  templateUrl: './create-reference-value-sheet.component.html',
  styleUrls: ['./create-reference-value-sheet.component.scss'],
  imports: [
    PageDetailComponent,
    MatButton,
    MatIcon,
    NgIf,
    PrintPageComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    TableComponent,
    NgStyle,
    AgrNumberDirective,
    SummaryCardComponent,
    AsyncPipe,
    TranslatePipe,
    AgrDatePipe,
  ],
  providers: [DatePipe],
})
export class CreateReferenceValueSheetComponent implements OnInit, AfterViewInit {
  readonly routeUrl$: Observable<string> = this.store.pipe(select(RouterSelectors.getPath));
  readonly user$: Observable<AgrUser> = this.store.select(UserSelectors.selectUser);
  readonly customer$: Observable<SelectedCustomer> = this.store.select(
    UserSelectors.selectCustomer,
  );
  readonly location$: Observable<SelectedLocation> = this.store.select(
    UserSelectors.selectLocation,
  );
  readonly columnSettings$: Observable<string[]> = this.store.select(getColumnSettings);

  readonly viewMode$: Observable<ViewMode> = this.routeUrl$.pipe(
    map((url: string) => {
      if (url.indexOf('reference-value-sheet/view/') >= 0) {
        return ViewMode.View;
      } else if (url.endsWith('reference-value-sheet/create')) {
        return ViewMode.Create;
      } else {
        return ViewMode.Edit;
      }
    }),
  );

  readonly pageTitle$: Observable<string> = this.viewMode$.pipe(
    take(1),
    map((viewMode) => {
      const title = this.locale.translate('reference-value-sheet.create.title');
      switch (viewMode) {
        case ViewMode.View:
          return `${title}: ${this.refValSheet?.name}`;
        case ViewMode.Create:
          return `${this.locale.translate('add')} ${title}`;
        case ViewMode.Edit:
          return `${this.locale.translate('edit')} ${title}: ${this.refValSheet?.name}`;
      }
    }),
  );

  /* TEMPLATES */
  @ViewChild('deleteRowTemplate') deleteRowTemplate: TemplateRef<unknown>;

  /* TABLE CONFIG */
  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource([] as ReferenceValueSheetRow[]),
    selectColumn: false,
    pagination: false,
    columns: [],
    enableArrowKeys: true,
  };

  readonly temperatureInsideControls = new FormArray([]);
  readonly relativeHumidityControls = new FormArray([]);
  readonly feedIntakeControls = new FormArray([]);
  readonly waterIntakeControls = new FormArray([]);
  readonly weightControls = new FormArray([]);
  readonly mortalityControls = new FormArray([]);
  readonly lightHourControls = new FormArray([]);
  readonly luxControls = new FormArray([]);

  readonly refValSheetGroup = new FormGroup({
    name: new FormControl(null, [Validators.required]),
    description: new FormControl(null),
    numberOfDays: new FormControl(null),
  });

  public readonly viewModes: typeof ViewMode = ViewMode;

  refValSheet: ReferenceValueSheet;
  isNewRefValSheet: boolean;

  tableColumns: TableColumn[];
  displayedTableColumns: TableColumn[];

  constructor(
    private readonly store: Store<RootState>,
    @Inject(LocaleService) private readonly locale: LocaleService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly location: Location,
    private readonly datePipe: DatePipe,
    private readonly dialogService: DialogService,
    private readonly settingsService: SettingsServiceHelper,
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(
        withLatestFrom(
          this.viewMode$,
          this.columnSettings$,
          this.store.select(getReferenceValueSheets),
        ),
        take(1),
      )
      .subscribe(([params, viewMode, columnSettings, refvalsheets]) => {
        if (viewMode === ViewMode.View) {
          this.tableConfig = {
            dataSource: new MatTableDataSource([] as ReferenceValueSheetRow[]),
            selectColumn: false,
            pagination: false,
            columns: [
              {
                id: ColumnSettings.DAY,
                type: TableColumnTypes.STRING,
                label: this.locale.translate('reference-value-sheet.create.table-header.day'),
              },
              {
                id: ColumnSettings.TEMPERATURE_INSIDE,
                type: TableColumnTypes.NUMBER,
                label: this.locale.translate(
                  'reference-value-sheet.create.table-header.temperatureInside',
                ),
              },
              {
                id: ColumnSettings.RELATIVE_HUMIDITY,
                type: TableColumnTypes.NUMBER,
                label: this.locale.translate(
                  'reference-value-sheet.create.table-header.relativeHumidity',
                ),
              },
              {
                id: ColumnSettings.FEED_INTAKE,
                type: TableColumnTypes.NUMBER,
                label: this.locale.translate(
                  'reference-value-sheet.create.table-header.feedIntakePerChickPerDay',
                ),
              },
              {
                id: ColumnSettings.WATER_INTAKE,
                type: TableColumnTypes.NUMBER,
                label: this.locale.translate(
                  'reference-value-sheet.create.table-header.waterIntake',
                ),
              },
              {
                id: ColumnSettings.WEIGHT,
                type: TableColumnTypes.NUMBER,
                label: this.locale.translate('reference-value-sheet.create.table-header.weight'),
              },
              {
                id: ColumnSettings.MORTALITY,
                type: TableColumnTypes.NUMBER,
                label: this.locale.translate('reference-value-sheet.create.table-header.mortality'),
              },
              {
                id: ColumnSettings.LIGHT_HOURS,
                type: TableColumnTypes.NUMBER,
                label: this.locale.translate(
                  'reference-value-sheet.create.table-header.numberOfHoursLight',
                ),
              },
              {
                id: ColumnSettings.LUX,
                type: TableColumnTypes.NUMBER,
                label: this.locale.translate(
                  'reference-value-sheet.create.table-header.lightIntensityInLux',
                ),
              },
            ],
            scrolling: true,
            width: '1200px',
            wrapperWidth: '100%',
            dragDrop: false,
          };
        } else {
          this.tableConfig = {
            dataSource: new MatTableDataSource([] as ReferenceValueSheetRow[]),
            selectColumn: false,
            pagination: false,
            columns: [
              {
                id: ColumnSettings.DAY,
                type: TableColumnTypes.STRING,
                label: this.locale.translate('reference-value-sheet.create.table-header.day'),
              },
              {
                id: ColumnSettings.TEMPERATURE_INSIDE,
                type: TableColumnTypes.INPUT_NUMBER,
                inputConfig: {
                  step: 1,
                  formArray: this.temperatureInsideControls,
                },
                label: this.locale.translate(
                  'reference-value-sheet.create.table-header.temperatureInside',
                ),
              },
              {
                id: ColumnSettings.RELATIVE_HUMIDITY,
                type: TableColumnTypes.INPUT_NUMBER,
                inputConfig: {
                  step: 1,
                  formArray: this.relativeHumidityControls,
                },
                label: this.locale.translate(
                  'reference-value-sheet.create.table-header.relativeHumidity',
                ),
              },
              {
                id: ColumnSettings.FEED_INTAKE,
                type: TableColumnTypes.INPUT_NUMBER,
                inputConfig: {
                  step: 1,
                  formArray: this.feedIntakeControls,
                },
                label: this.locale.translate(
                  'reference-value-sheet.create.table-header.feedIntakePerChickPerDay',
                ),
              },
              {
                id: ColumnSettings.WATER_INTAKE,
                type: TableColumnTypes.INPUT_NUMBER,
                inputConfig: {
                  step: 1,
                  formArray: this.waterIntakeControls,
                },
                label: this.locale.translate(
                  'reference-value-sheet.create.table-header.waterIntake',
                ),
              },
              {
                id: ColumnSettings.WEIGHT,
                type: TableColumnTypes.INPUT_NUMBER,
                inputConfig: {
                  step: 0.001,
                  formArray: this.weightControls,
                },
                label: this.locale.translate('reference-value-sheet.create.table-header.weight'),
              },
              {
                id: ColumnSettings.MORTALITY,
                type: TableColumnTypes.INPUT_NUMBER,
                inputConfig: {
                  step: 0.1,
                  formArray: this.mortalityControls,
                },
                label: this.locale.translate('reference-value-sheet.create.table-header.mortality'),
              },
              {
                id: ColumnSettings.LIGHT_HOURS,
                type: TableColumnTypes.INPUT_NUMBER,
                inputConfig: {
                  step: 1,
                  formArray: this.lightHourControls,
                },
                label: this.locale.translate(
                  'reference-value-sheet.create.table-header.numberOfHoursLight',
                ),
              },
              {
                id: ColumnSettings.LUX,
                type: TableColumnTypes.INPUT_NUMBER,
                inputConfig: {
                  step: 1,
                  formArray: this.luxControls,
                },
                label: this.locale.translate(
                  'reference-value-sheet.create.table-header.lightIntensityInLux',
                ),
              },
              {
                id: ColumnSettings.DELETE_ROW,
                type: TableColumnTypes.TEMPLATE,
                label: '',
              },
            ],
            scrolling: true,
            width: '1200px',
            wrapperWidth: '100%',
            enableArrowKeys: true,
          };
        }
        this.refValSheetGroup.controls.numberOfDays.setValue(0);

        this.tableColumns = this.tableConfig.columns;
        this.displayedTableColumns = this.tableColumns.filter((x) => columnSettings.includes(x.id));

        this.tableConfig = {
          ...this.tableConfig,
          columns: this.displayedTableColumns,
        };
        const referenceValueSheet = refvalsheets.find((rvs) => rvs.id === parseInt(params.id, 10));
        if (referenceValueSheet) {
          // EDIT
          this.isNewRefValSheet = false;
          this.refValSheet = { ...(<ReferenceValueSheet>referenceValueSheet) };
          this.refValSheetGroup.controls.name.setValue(this.refValSheet.name);
          this.refValSheetGroup.controls.description.setValue(this.refValSheet.description);
          this.tableConfig.dataSource.data = this.refValSheet.referenceValueSheetRows
            .map((row) => Object.assign({}, row))
            .sort((a, b) => (a.id > b.id ? 1 : -1));
        } else {
          // CREATE NEW
          this.isNewRefValSheet = true;
          this.settingsService
            .getSingle(null, PoultryContextKeys.REFERENCE_VALUE_SHEET, SettingsStorage.AppLocal)
            .subscribe((base: ReferenceValueSheet) => {
              if (base !== null && base !== undefined) {
                this.refValSheet = {
                  ...base,
                  isStandardReferenceValueSheet: true,
                  standardReferenceValueSheetId: base.id as number,
                  country: null,
                  id: 0,
                };

                this.refValSheetGroup.controls.name.setValue(base?.name as string);
                this.refValSheetGroup.controls.description.setValue(base?.description as string);
                this.tableConfig.dataSource.data =
                  base.referenceValueSheetRows as ReferenceValueSheetRow[];
              } else {
                this.refValSheet = <ReferenceValueSheet>{};
                this.tableConfig.dataSource.data = [];
              }
            });
        }
      });
  }

  ngAfterViewInit() {
    // Set deleterow column template.
    this.viewMode$.pipe(take(1)).subscribe((viewMode) => {
      if (viewMode !== ViewMode.View) {
        setTimeout(() => {
          this.displayedTableColumns.push(
            this.tableColumns.find((c) => c.id === ColumnSettings.DELETE_ROW),
          );

          this.tableConfig = {
            ...this.tableConfig,
            columns: this.displayedTableColumns,
          };

          const newConfig = { ...this.tableConfig };
          const columnDeleteRow = newConfig.columns.find((c) => c.id === ColumnSettings.DELETE_ROW);

          columnDeleteRow.template = this.deleteRowTemplate;
          this.tableConfig = newConfig;
        }, 50);
      }
    });
  }

  hasDayValueRows(): boolean {
    return this.tableConfig.dataSource.data.length > 0;
  }

  isLastRow(refValSheetRow: ReferenceValueSheetRow): boolean {
    const lastDay = Math.max(
      ...this.tableConfig.dataSource.data.map((row: ReferenceValueSheetRow) => row.day),
    );
    return refValSheetRow.day === lastDay;
  }

  hasNoNumberOfDays(): boolean {
    const nrOfDays = <number>this.refValSheetGroup.controls.numberOfDays.value;
    return !nrOfDays || nrOfDays === 0;
  }

  deleteDayValuesRow(refValSheetRow: ReferenceValueSheetRow) {
    this.updateDataSource();
    this.tableConfig.dataSource.data = [
      ...this.tableConfig.dataSource.data
        .filter((row: ReferenceValueSheetRow) => row.id !== refValSheetRow.id)
        .sort((a: ReferenceValueSheetRow, b: ReferenceValueSheetRow) => (a.id > b.id ? 1 : -1)),
    ];
  }

  addOneDayValuesRow() {
    this.updateDataSource();
    this.tableConfig.dataSource.data = [
      ...this.tableConfig.dataSource.data,
      {
        id:
          this.tableConfig.dataSource.data.length > 0
            ? Math.max(
                ...this.tableConfig.dataSource.data.map((row: ReferenceValueSheetRow) => row.id),
              ) + 1
            : 0,
        day:
          this.tableConfig.dataSource.data.length > 0
            ? Math.max(
                ...this.tableConfig.dataSource.data.map((row: ReferenceValueSheetRow) => row.day),
              ) + 1
            : 1,
        temperatureInside: null,
        relativeHumidity: null,
        feedIntakePerChickPerDay: null,
        waterIntake: null,
        weight: null,
        mortality: null,
        numberOfHoursLight: null,
        lightIntensityInLux: null,
      },
    ];
  }

  addMultipleDayValuesRows() {
    const nrOfDays = <number>this.refValSheetGroup.controls.numberOfDays.value;
    if (nrOfDays > 0 && this.tableConfig.dataSource.data.length === 0) {
      for (let day = 0; day < nrOfDays; day++) {
        this.tableConfig.dataSource.data = [
          ...this.tableConfig.dataSource.data,
          {
            id: day,
            day,
            temperatureInside: null,
            relativeHumidity: null,
            feedIntakePerChickPerDay: null,
            waterIntake: null,
            weight: null,
            mortality: null,
            numberOfHoursLight: null,
            lightIntensityInLux: null,
          },
        ];
      }
    }
  }

  printRefValSheet() {
    const existingTitle = document.title;
    const proposedTitle = `PoultryNEXT ${this.locale.translate(
      'reference-value-sheet.create.title',
    )} - ${this.datePipe.transform(Date())}`;
    if (window) {
      document.title = proposedTitle;
      window.print();
      document.title = existingTitle;
    }
  }

  openColumnSettings() {
    this.dialogService
      .form(ColumnSettingsComponent, {})
      .pipe(withLatestFrom(this.viewMode$), take(1))
      .subscribe(([result, viewMode]) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          this.displayedTableColumns = this.tableColumns.filter((x) =>
            result.result?.displayedColumns.includes(x.id),
          );
          if (viewMode !== ViewMode.View) {
            this.updateDataSource();
            this.displayedTableColumns.push(
              this.tableColumns.find((c) => c.id === ColumnSettings.DELETE_ROW),
            );
          }
          this.tableConfig = {
            ...this.tableConfig,
            columns: this.displayedTableColumns,
          };
        }
      });
  }

  back(saved = false) {
    if (this.refValSheet && saved) {
      // Simply go back to the previous page, which is the balance screen of the cycle.
      this.location.back();
    } else {
      if (this.router.navigated) {
        this.location.back();
      } else {
        this.router.navigate(['../'], { relativeTo: this.activatedRoute });
      }
    }
  }

  private updateDataSource() {
    const refValSheetRows = this.tableConfig.dataSource.data.map((row: ReferenceValueSheetRow) =>
      Object.assign({}, row),
    );

    refValSheetRows.forEach((row) => {
      row.temperatureInside = <number>this.temperatureInsideControls.value[row.id];
      row.relativeHumidity = <number>this.relativeHumidityControls.value[row.id];
      row.feedIntakePerChickPerDay = <number>this.feedIntakeControls.value[row.id];
      row.waterIntake = <number>this.waterIntakeControls.value[row.id];
      row.weight = <number>this.weightControls.value[row.id];
      row.mortality = <number>this.mortalityControls.value[row.id];
      row.numberOfHoursLight = <number>this.lightHourControls.value[row.id];
      row.lightIntensityInLux = <number>this.luxControls.value[row.id];
    });
    this.tableConfig.dataSource.data = refValSheetRows;
  }

  private validateRefValSheet(): boolean {
    this.refValSheetGroup.markAllAsTouched();
    if (this.refValSheetGroup.valid) {
      return true;
    }
    let description = '';
    if (!this.refValSheetGroup.valid) {
      description += `<li>${this.locale.translate(
        'reference-value-sheet.create.validation-error.refvalsheet-name',
      )}</li>`;
    }

    this.dialogService.notify({
      title: this.locale.translate('reference-value-sheet.create.validation-error.title'),
      text: `<ul>${description}</ul>`,
    });
    return false;
  }

  save() {
    if (this.validateRefValSheet()) {
      this.updateDataSource();
      this.user$.subscribe((user) => {
        this.refValSheet = {
          ...this.refValSheet,
          country: 'NL',
          updatedAdvisorName: user.name,
          isStandardReferenceValueSheet: true,
          isActive: true,
          createdAdvisorName: this.isNewRefValSheet
            ? user.name
            : this.refValSheet.createdAdvisorName,
          name: <string>this.refValSheetGroup.controls.name.value,
          description: <string>this.refValSheetGroup.controls.description.value,
          temperatureUnit: TemperatureUnit.Celcius,
          feedUnit: FeedUnit.Gram,
          waterUnit: WaterUnit.ML,
          weightUnit: WeightUnit.Gram,
          animalType: AnimalType.REARING,
          referenceValueSheetRows: <ReferenceValueSheetRow[]>this.tableConfig.dataSource.data.map(
            (row: ReferenceValueSheetRow) => ({
              id: row.id,
              day: row.day,
              temperatureInside: this.displayedTableColumns.some(
                (x) => x.id === ColumnSettings.TEMPERATURE_INSIDE,
              )
                ? row.temperatureInside
                : null,
              relativeHumidity: this.displayedTableColumns.some(
                (x) => x.id === ColumnSettings.RELATIVE_HUMIDITY,
              )
                ? row.relativeHumidity
                : null,
              feedIntakePerChickPerDay: this.displayedTableColumns.some(
                (x) => x.id === ColumnSettings.FEED_INTAKE,
              )
                ? row.feedIntakePerChickPerDay
                : null,
              waterIntake: this.displayedTableColumns.some(
                (x) => x.id === ColumnSettings.WATER_INTAKE,
              )
                ? row.waterIntake
                : null,
              weight: this.displayedTableColumns.some((x) => x.id === ColumnSettings.WEIGHT)
                ? row.weight
                : null,
              mortality: this.displayedTableColumns.some((x) => x.id === ColumnSettings.MORTALITY)
                ? row.mortality
                : null,
              numberOfHoursLight: this.displayedTableColumns.some(
                (x) => x.id === ColumnSettings.LIGHT_HOURS,
              )
                ? row.numberOfHoursLight
                : null,
              lightIntensityInLux: this.displayedTableColumns.some(
                (x) => x.id === ColumnSettings.LUX,
              )
                ? row.lightIntensityInLux
                : null,
            }),
          ),
        };
        this.store.dispatch(
          refValSheetActions.upsertRefValSheetRequest({ refValSheet: this.refValSheet }),
        );
      });
    }
  }
}
